import { useId, useState } from "react";
import { useShareGate } from "@/hooks/useShareGate";
import { ToolContainer } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { sortText, type SortOptions } from "./sorter";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";

const TOOL_INFO_CONTENT = {
  what: `Sorts a list of lines alphabetically (with natural number order), by length, or shuffles them, with options to reverse the order, ignore case, and remove blank lines or duplicates — useful for cleaning up a pasted list before using it somewhere else, or just putting a long list into a consistent, predictable order.`,
  how: `Paste your list (one item per line), choose your sorting options, and copy the sorted result.`,
  tip: `Leave "Natural numbers" on for file names and numbered items (item 2 comes before item 10). Turn "Ignore case" off only to separate Apple from apple (the capital version comes first); a lowercase word such as "apple" still sorts before "Banana". "Remove duplicates" follows the Ignore case and Trim spaces settings and keeps the first copy.`,
};

export default function TextSorter() {
  const { gateShare } = useShareGate();
  const [text, setText] = useState("");
  const [reverse, setReverse] = useState(false);
  const [caseInsensitive, setCaseInsensitive] = useState(true);
  const [natural, setNatural] = useState(true);
  const [trim, setTrim] = useState(true);
  const [removeBlank, setRemoveBlank] = useState(true);
  const [removeDupes, setRemoveDupes] = useState(false);
  const [undoText, setUndoText] = useState<string | null>(null);
  const [summary, setSummary] = useState("");

  const { logUsageNow } = useLogUsageOnAction();

  const run = async (mode: SortOptions["mode"]) => {
    const allowed = await logUsageNow();
    if (!allowed) return;
    const r = sortText(text, { mode, reverse, ignoreCase: caseInsensitive, natural, trim, removeBlank, removeDupes });
    setUndoText(text);
    setText(r.text);
    setSummary(`${r.before} lines → ${r.after}${r.before !== r.after ? ` (${r.before - r.after} removed)` : ""}`);
    toast.success(mode === "shuffle" ? "Shuffled!" : mode === "none" ? "Cleaned!" : "Sorted!");
  };

  const copyResult = () => gateShare(async () => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); return true; }
    catch { toast.error("Couldn't copy. Select the text and copy it by hand."); return false; }
  });

  const uid = useId();
  const Sw = ({ checked, set, label }: { checked: boolean; set: (v: boolean) => void; label: string }) => {
    const id = `${uid}-${label.replace(/\W+/g, "-")}`;
    return <div className="flex items-center gap-2"><Switch id={id} checked={checked} onCheckedChange={set} /><Label htmlFor={id} className="text-sm">{label}</Label></div>;
  };

  return (
    <ToolContainer>
      <Textarea
        aria-label="Lines to sort" placeholder="Paste your lines of text here, one per line..."
        value={text}
        onChange={e => { setText(e.target.value); setSummary(""); }}
        className="min-h-[250px] font-mono text-sm"
      />
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-4">
        <Sw checked={reverse} set={setReverse} label="Reverse (Z→A)" />
        <Sw checked={caseInsensitive} set={setCaseInsensitive} label="Ignore case (a = A)" />
        <Sw checked={natural} set={setNatural} label="Natural numbers (2 before 10)" />
        <Sw checked={trim} set={setTrim} label="Trim spaces" />
        <Sw checked={removeBlank} set={setRemoveBlank} label="Remove blank lines" />
        <Sw checked={removeDupes} set={setRemoveDupes} label="Remove duplicates" />
      </div>
      <div className="flex flex-wrap gap-3 mt-4">
        <Button onClick={() => run("alpha")} disabled={!text.trim()}>Sort Alphabetically</Button>
        <Button variant="outline" onClick={() => run("length")} disabled={!text.trim()}>Sort by Length</Button>
        <Button variant="outline" onClick={() => run("shuffle")} disabled={!text.trim()}>Shuffle</Button>
        <Button variant="outline" onClick={() => run("none")} disabled={!text.trim()}>Clean only (keep order)</Button>
        <Button variant="outline" onClick={copyResult} disabled={!text.trim()}>Copy Result</Button>
        {undoText !== null && <Button variant="ghost" onClick={() => { setText(undoText); setUndoText(null); setSummary(""); }}>Undo</Button>}
      </div>
      {summary && <p className="text-sm text-muted-foreground mt-3" data-testid="sort-summary">{summary}</p>}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
